import type { AuthProvider } from '@refinedev/core';

import { supabaseClient } from './supabase';

/**
 * Entrada do painel: e-mail com código de 6 dígitos, o mesmo caminho do site. Não existe senha.
 *
 * O `login` do refine cuida só da **conferência do código** — pedir o código é
 * `enviarCodigo()`, chamado direto pela tela. Separar os dois evita o engano de devolver
 * `success: false` só porque o código acabou de ser enviado, o que o refine mostraria como erro.
 *
 * Entrar não basta: `check()` confirma que o usuário é admin de verdade, lendo
 * `app_metadata.role` do JWT. Quem não é admin é posto para fora com o motivo na tela — a RLS
 * já barraria qualquer leitura, mas é melhor dizer o porquê do que mostrar telas vazias.
 */
function ehAdmin(appMetadata: Record<string, unknown> | undefined): boolean {
  return appMetadata?.['role'] === 'admin';
}

/**
 * Pede o código de 6 dígitos.
 *
 * `shouldCreateUser: false` é deliberado: o painel não cria conta. A conta administradora é
 * criada no site (ou no painel do Supabase) e marcada à mão com `app_metadata.role = 'admin'`.
 */
export async function enviarCodigo(email: string): Promise<{ erro: string | null }> {
  const { error } = await supabaseClient.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: false },
  });

  if (!error) return { erro: null };

  // Não revela se o e-mail existe: o painel é alvo óbvio de tentativa de descoberta de conta.
  return { erro: 'Não deu para enviar o código. Confira o e-mail digitado.' };
}

export const authProvider: AuthProvider = {
  async login({ email, code }: { email: string; code: string }) {
    const { data, error } = await supabaseClient.auth.verifyOtp({
      email,
      token: code,
      type: 'email',
    });

    if (error) {
      return { success: false, error: { name: 'login', message: 'O código não confere.' } };
    }

    if (!ehAdmin(data.user?.app_metadata)) {
      await supabaseClient.auth.signOut();
      return {
        success: false,
        error: {
          name: 'login',
          message:
            'Esta conta não é administradora. Marque app_metadata.role = "admin" no painel do Supabase.',
        },
      };
    }

    return { success: true, redirectTo: '/series' };
  },

  async logout() {
    await supabaseClient.auth.signOut();
    return { success: true, redirectTo: '/login' };
  },

  async check() {
    const { data } = await supabaseClient.auth.getSession();
    const user = data.session?.user;

    if (!user || !ehAdmin(user.app_metadata)) {
      return { authenticated: false, redirectTo: '/login' };
    }

    return { authenticated: true };
  },

  async getIdentity() {
    const { data } = await supabaseClient.auth.getUser();
    if (!data.user) return null;

    return { id: data.user.id, name: data.user.email ?? 'admin' };
  },

  async onError(error) {
    return { error };
  },
};
